import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { PageContainer } from "~/components/layout/PageContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useProfileStore } from "~/store/profileStore";

import ChannelTab from "./components/ChannelTab";
import SalesGoalsHeader from "./components/SalesGoalsHeader";
import SellerClientTab from "./components/SellerClientTab";
import SellerTab from "./components/SellerTab";
import { useSalesGoals } from "./hooks/useSalesGoals";
import type { AnalysisViewMode } from "./types/salesGoals";

export default function SalesGoalsDashboard() {
  const [year, setYear] = useState(dayjs().year());
  const [viewMode, setViewMode] = useState<AnalysisViewMode>("yearly");
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);

  const { id: profileId, role } = useProfileStore();
  const isSeller = role === "seller";
  const [activeTab, setActiveTab] = useState(
    isSeller ? "seller-client" : "seller"
  );

  const tabOptions = [
    ...(!isSeller ? [{ value: "seller", label: "Por Vendedor" }] : []),
    {
      value: "seller-client",
      label: isSeller ? "Por Cliente" : "Por Vendedor / Cliente",
    },
    { value: "channel", label: "Por Canal" },
  ];

  const { isLoading, bySeller, clients, currentMonth } = useSalesGoals(
    year,
    isSeller ? profileId : undefined
  );

  useEffect(() => {
    if (currentMonth > 0) setSelectedMonth(currentMonth);
  }, [currentMonth]);

  return (
    <PageContainer>
      <SalesGoalsHeader
        year={year}
        onYearChange={setYear}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Mobile: dropdown selector */}
        <div className="sm:hidden">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tabOptions.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: tabs */}
        <TabsList className="hidden w-full justify-start sm:flex">
          {tabOptions.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {!isSeller && (
          <TabsContent value="seller">
            <SellerTab
              sellers={bySeller}
              currentMonth={currentMonth}
              isLoading={isLoading}
              viewMode={viewMode}
              selectedMonth={selectedMonth}
              year={year}
            />
          </TabsContent>
        )}
        <TabsContent value="seller-client">
          <SellerClientTab
            clients={clients}
            sellers={bySeller}
            currentMonth={currentMonth}
            isLoading={isLoading}
            viewMode={viewMode}
            selectedMonth={selectedMonth}
            year={year}
            hideSelllerFilter={isSeller}
          />
        </TabsContent>
        <TabsContent value="channel">
          <ChannelTab
            clients={clients}
            currentMonth={currentMonth}
            isLoading={isLoading}
            viewMode={viewMode}
            selectedMonth={selectedMonth}
            year={year}
          />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
